import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import RfPageFrame from '../layout/RfPageFrame';

export default function SolutionsTrustScore() {
  return (
    <>
      <div className="rf-hero-band">
        <RfPageFrame className="!py-10 md:!py-14">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5c6d7e] mb-2">Solutions · Human Trust · TrustScore</p>
          <h1 className="text-2xl md:text-[2.25rem] font-semibold text-[#2c2c2c] tracking-tight max-w-3xl leading-tight">
            TrustScore — Human risk assessment for the AI era
          </h1>
          <p className="mt-6 text-[1.05rem] text-[#5c5c5c] max-w-2xl leading-relaxed">
            Quantify your organisation's human risk exposure across five dimensions. Baseline assessment, targeted training, re-assessment — benchmarked against industry peers and delivered as a board-ready report.
          </p>
          <span className="inline-block mt-4 text-xs px-3 py-1.5 rounded bg-[#eef4fb] text-[#185FA5] font-medium">Entry point · Fixed fee $15–30K</span>
        </RfPageFrame>
      </div>

      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c] mb-8">Five scoring dimensions</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { num: '01', title: 'AI Threat Recognition', desc: 'Can employees identify prompt injection, deepfakes, and AI-generated social engineering in real-world contexts?' },
              { num: '02', title: 'Verification Behaviour', desc: 'Do employees use out-of-band verification for high-risk requests from executives or when copilot outputs look anomalous?' },
              { num: '03', title: 'Reporting Instincts', desc: 'Do employees report suspicious AI outputs, anomalous copilot behaviour, and failed vishing attempts to the security team?' },
              { num: '04', title: 'Policy Compliance', desc: 'Are employees following acceptable use policies for copilots and generative AI tools in their daily workflows?' },
              { num: '05', title: 'Shadow AI Usage', desc: 'Are unsanctioned AI tools in use, and do employees understand the data and security risks they carry?' },
            ].map(({ num, title, desc }) => (
              <div key={num} className="rf-card p-6 bg-white space-y-3">
                <div className="text-2xl font-bold text-[#005a9c] opacity-30">{num}</div>
                <h3 className="font-semibold text-[#2c2c2c]">{title}</h3>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <div className="rf-card p-8 md:p-10 bg-white border-dashed border-2">
            <h2 className="text-xl font-semibold text-[#2c2c2c] mb-6">Delivery model</h2>
            <div className="space-y-6">
              {[
                { step: '01', title: 'Baseline Assessment', desc: '2-week engagement combining surveys, simulated phishing and vishing tests, policy review, and shadow AI discovery across the organisation.' },
                { step: '02', title: 'Scoring & Gap Analysis', desc: 'Scores across all five dimensions, benchmarked against industry peers. Prioritised gap analysis with a remediation roadmap your team can act on immediately.' },
                { step: '03', title: 'Targeted Remediation', desc: 'Tailored training and awareness interventions mapped to identified gaps — drawing from the AI Awareness Academy and SimStrike simulation campaigns.' },
                { step: '04', title: 'Re-assessment & Board Report', desc: 'Post-remediation re-assessment with trend analysis. Board-ready report with quantified risk reduction and evidence for cyber insurers.' },
              ].map(({ step, title, desc }) => (
                <div key={step} className="flex gap-4">
                  <div className="font-bold text-[#005a9c] text-lg opacity-40 shrink-0">{step}</div>
                  <div>
                    <h4 className="font-semibold text-[#2c2c2c]">{title}</h4>
                    <p className="text-sm text-[#5c5c5c] mt-1 leading-relaxed">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-6">
            <Link to="/solutions/deepstrike" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c] hover:text-[#004a82]">
              DeepStrike — managed simulations <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/solutions/copilotready" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c] hover:text-[#004a82]">
              CopilotReady — copilot readiness <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <p className="mt-10 text-[0.9375rem] text-[#5c5c5c]">
            <Link to="/solutions/security-for-ai" className="text-[#005a9c] font-medium hover:text-[#004a82]">
              ← Back to Security for AI
            </Link>
          </p>
        </RfPageFrame>
      </section>
    </>
  );
}
